var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { UXPContext } from "@core/context/Context";
import ToastContext from "./ToastContext";
import Toast from "./Toast";
import PortalContainer from "../../widget/popups/PortalContainer";
import useIsMobileLayout from "@hooks/useIsMobileLayout";
import classNames from "classnames";
import { isFakeMouse } from "@hooks/touch/touchRules";
import { useOverlayStack } from "@widget/popups/overlay-stack";
import ButtonComponent from "@widget/buttons/ButtonComponent";
// The drop-in time: the next toast in a burst waits this long
const ENTER_MS = 250;
// create a random id
function generateId() {
    const date = new Date();
    const random = Math.round(Math.random() * 10000);
    return date.getTime().toString() + random.toString();
}
// Default values for ToastProvider (excluding children)
const defaultToastProviderProps = {
    position: "top-right",
    autoClose: true,
    showCloseBtn: true,
    closeAfter: 5000,
};
// ToastProvider component
const ToastProvider = (props) => {
    var _a, _b;
    // Merge default props with provided props
    const mergedProps = Object.assign(Object.assign({}, defaultToastProviderProps), props);
    const context = React.useContext(UXPContext);
    const isMobile = useIsMobileLayout();
    const [toasts, setToasts] = React.useState([]);
    // Phones: the stack opens into a list on a tap
    const [expanded, setExpanded] = React.useState(false);
    const toastRef = React.useRef([]);
    const expandedRef = React.useRef(false);
    expandedRef.current = expanded;
    // Phones: Back (swipe or button) closes the open list, not the page under it
    useOverlayStack(isMobile && expanded, () => setExpanded(false));
    // Arrivals one at a time: a new toast lands over the front one (its drop-in), and only then
    // does the stack shift back. A burst waits in this queue.
    const queueRef = React.useRef([]);
    const enterTimerRef = React.useRef(null);
    const [enteringId, setEnteringId] = React.useState(null);
    const append = (toast) => {
        // Arrival order: a new toast is appended, so existing ones never move (no replayed animations)
        const _toasts = [...(toastRef.current || []), toast];
        toastRef.current = _toasts;
        setToasts(_toasts);
    };
    const showNext = React.useCallback(() => {
        const next = queueRef.current.shift();
        if (!next) {
            enterTimerRef.current = null;
            setEnteringId(null);
            return;
        }
        // Its countdown starts when it shows, not when it was queued
        append(Object.assign(Object.assign({}, next), { createdAt: new Date().getTime() }));
        setEnteringId(next.id);
        enterTimerRef.current = setTimeout(showNext, ENTER_MS);
    }, []);
    // The open list has no stack to animate: everything waiting shows at once
    React.useEffect(() => {
        if (!expanded || !queueRef.current.length)
            return;
        clearTimeout(enterTimerRef.current);
        enterTimerRef.current = null;
        const waiting = queueRef.current.map(t => (Object.assign(Object.assign({}, t), { createdAt: new Date().getTime() })));
        queueRef.current = [];
        waiting.forEach(append);
        setEnteringId(null);
    }, [expanded]);
    React.useEffect(() => () => clearTimeout(enterTimerRef.current), []);
    // add method
    const add = (content) => {
        const id = generateId();
        const createdAt = new Date().getTime();
        // Merge default ToastProvider props (except position) into content
        const { position } = mergedProps, rest = __rest(mergedProps, ["position"]);
        const updatedContent = Object.assign(Object.assign({}, rest), content);
        const newToast = { id, content: updatedContent, createdAt };
        if (expandedRef.current)
            return append(newToast);
        queueRef.current.push(newToast);
        if (!enterTimerRef.current)
            showNext();
    };
    // remove method 
    const remove = React.useCallback((id) => {
        const _toasts = toastRef.current.filter((toast) => toast.id !== id);
        toastRef.current = _toasts;
        setToasts(_toasts);
        if (!_toasts.length)
            setExpanded(false);
    }, []);
    // remove every toast (phones: swipe the stack up, or Clear all)
    const removeAll = React.useCallback(() => {
        queueRef.current = [];
        clearTimeout(enterTimerRef.current);
        enterTimerRef.current = null;
        setEnteringId(null);
        toastRef.current = [];
        setToasts([]);
        setExpanded(false);
    }, []);
    // Helper to update content with type
    const updatedContent = (content, type) => {
        const defaults = { type };
        if (typeof content === "string") {
            content = { content };
        }
        return Object.assign(Object.assign({}, defaults), content);
    };
    // Toast type methods
    const success = (content) => add(updatedContent(content, "success"));
    const error = (content) => add(updatedContent(content, "error"));
    const warning = (content) => add(updatedContent(content, "warning"));
    const info = (content) => add(updatedContent(content, "info"));
    const custom = (content) => add(updatedContent(content, "custom"));
    // Provider value
    const providerValue = React.useMemo(() => ({ success, error, warning, info, custom, remove, removeAll }), [remove, removeAll]);
    // Render a single toast
    const renderToast = (toast, peek, entering) => (_jsx(Toast, { icon: toast.content.icon, type: toast.content.type, title: toast.content.title, content: toast.content.content, onClose: toast.content.onClose || null, showCloseBtn: toast.content.showCloseBtn, autoClose: toast.content.autoClose, instance: toast.id, closeAfter: toast.content.closeAfter, createdAt: toast.createdAt, swipeable: isMobile, peek: peek === undefined ? undefined : Math.min(peek, 3), order: entering ? -1 : peek, entering: entering, paused: expanded, onSwipeUp: peek === 0 && !expanded ? removeAll : undefined }, toast.id));
    // Places count only toasts that have landed; the one entering is drawn over the front
    const landed = toasts.filter(t => t.id !== enteringId);
    // Calculate container position; adjust for RTL if needed
    const containerPosition = (context === null || context === void 0 ? void 0 : context.direction) === "rtl"
        ? mergedProps.position
            .replace("right", "temp")
            .replace("left", "right")
            .replace("temp", "left")
        : mergedProps.position;
    return (_jsxs(ToastContext.Provider, { value: providerValue, children: [_jsxs(PortalContainer, { disableScroll: false, children: [isMobile && expanded && _jsx("div", { className: "uxpcore_toast__backdrop", onClick: () => setExpanded(false) }), toasts.length > 0 && _jsxs("div", { className: classNames("uxpcore_toast__container uxpcore_toast__container--stack", isMobile ? "uxpcore_toast__container--phone" : containerPosition, expanded ? "uxpcore_toast__container--expanded" : "uxpcore_toast__container--stacked"), 
                        // Phones open the list with a tap; the web while the mouse is over the stack
                        onClick: isMobile ? () => { if (!expanded)
                            setExpanded(true); } : undefined, onMouseEnter: isMobile ? undefined : () => { if (!isFakeMouse())
                            setExpanded(true); }, onMouseLeave: isMobile ? undefined : () => setExpanded(false), children: [_jsx("div", { className: "uxpcore_toast__list", children: toasts.map(t => t.id === enteringId && landed.length > 0
                                    ? renderToast(t, 0, true)
                                    : renderToast(t, landed.length - 1 - landed.indexOf(t))) }), !expanded && landed.length > 1 && _jsxs("div", { className: "uxpcore_toast__more", children: ["+", landed.length - 1] }), expanded && (isMobile || toasts.length > 1) && _jsxs("div", { className: "uxpcore_toast__actions", children: [isMobile && _jsx(ButtonComponent, { title: (_a = context === null || context === void 0 ? void 0 : context.$L) === null || _a === void 0 ? void 0 : _a.call(context, 'uxp-core.text.close'), variant: "secondary", onClick: () => setExpanded(false) }), _jsx(ButtonComponent, { title: (_b = context === null || context === void 0 ? void 0 : context.$L) === null || _b === void 0 ? void 0 : _b.call(context, 'uxp-core.text.clear-all'), variant: isMobile ? 'primary' : 'secondary', size: isMobile ? undefined : 'small', onClick: removeAll })] })] })] }), props.children] }));
};
export default ToastProvider;
